import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { trackClick, trackFunnelStep } from '@/utils/analytics';
import { useEmailConfirmation } from '@/hooks/useEmailConfirmation';
import { getAppUrl } from '@/utils/authHelpers';
import { getRedirectTo, logAuthRedirect } from '@/utils/authRedirect';
import { EmailOnlyStep } from './EmailOnlyStep';
import { EmailVerificationStep } from './EmailVerificationStep';
import { CreatePasswordStep } from './CreatePasswordStep';
import { ProfileInfoStep } from './ProfileInfoStep';

interface InitialFormData {
  name: string;
  age: string;
  state: string;
  coverageAmount: string;
  smokingStatus: string;
}

interface ProgressiveSignupProps {
  initialData: InitialFormData;
  onSignupComplete: (user: any) => void;
}

enum Step {
  EmailOnly = 'emailOnly',
  VerifyEmail = 'verifyEmail', 
  CreatePassword = 'createPassword',
  ProfileInfo = 'profileInfo',
  Done = 'done'
}

type SignupStep = Step;

interface SignupState {
  email: string;
  emailSentAt?: number;
  verificationAttempts: number;
  userId?: string;
  emailVerified: boolean;
  hasPassword: boolean;
  profileComplete: boolean;
}

export const ProgressiveSignup: React.FC<ProgressiveSignupProps> = ({
  initialData,
  onSignupComplete
}) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<SignupStep>(Step.EmailOnly);
  const [signupState, setSignupState] = useState<SignupState>({
    email: '',
    verificationAttempts: 0,
    emailVerified: false,
    hasPassword: false,
    profileComplete: false
  });
  const [isLoading, setIsLoading] = useState(false);

  // Check authentication state and resume at correct step
  useEffect(() => {
    const checkAuthState = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed, first_name, last_name')
          .eq('user_id', session.user.id)
          .maybeSingle();

        // Update signup state based on current user status
        setSignupState(prev => ({
          ...prev,
          email: session.user.email || '',
          userId: session.user.id,
          emailVerified: !!session.user.email_confirmed_at,
          hasPassword: true, // If they have a session, they have a password
          profileComplete: !!profile?.onboarding_completed
        }));

        // Route to correct step based on completion status
        if (profile?.onboarding_completed) {
          onSignupComplete(session.user);
        } else if (!session.user.email_confirmed_at) {
          setCurrentStep(Step.VerifyEmail);
        } else if (!profile?.first_name || !profile?.last_name) {
          setCurrentStep(Step.ProfileInfo);
        } else {
          onSignupComplete(session.user);
        }
      } else {
        // Check for pending verification state in localStorage
        const pendingEmail = localStorage.getItem('safurra_pending_email');
        const pendingUserId = localStorage.getItem('safurra_pending_user_id');
        
        if (pendingEmail && pendingUserId) {
          setSignupState(prev => ({
            ...prev,
            email: pendingEmail,
            userId: pendingUserId,
            emailVerified: false,
            hasPassword: false,
            profileComplete: false
          }));
          setCurrentStep(Step.VerifyEmail);
        }
      }
    };

    checkAuthState();
  }, [onSignupComplete]);

  // Listen for email confirmation across tabs
  useEmailConfirmation(() => {
    toast({
      title: "Email confirmed ✅",
      description: "Continuing with your account setup...",
    });
    
    // The auth state listener will handle the actual routing
    // Just show the confirmation message here
  });

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    trackClick('signup_google_clicked', 'authentication');

    try {
      const redirectTo = getRedirectTo('/onboarding');
      logAuthRedirect('google-oauth', redirectTo);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            signup_flow: 'true'
          }
        }
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Google auth error:', error);
      toast({
        title: "Error",
        description: "Unable to sign in with Google. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handlePhoneAuth = async () => {
    setIsLoading(true);
    trackClick('signup_phone_clicked', 'authentication');
    
    // For now, show a toast that phone auth is coming soon
    // In a real implementation, you would implement phone number input and SMS OTP
    toast({
      title: "Coming Soon",
      description: "Phone number authentication will be available soon. Please use email or Google for now.",
    });
    setIsLoading(false);
  };

  const handleEmailSubmit = async (email: string) => {
    setIsLoading(true);
    trackClick('signup_email_submitted', 'authentication');

    try {
      const redirectTo = getRedirectTo('/onboarding');
      logAuthRedirect('email-signup', redirectTo);

      const { data, error } = await supabase.auth.signUp({
        email,
        password: 'TempPass123!', // User will set real password after email confirmation
        options: {
          emailRedirectTo: redirectTo,
          data: {
            requires_password_reset: true,
            flow: 'progressive',
            initial_data: JSON.stringify(initialData)
          }
        }
      });

      if (error) {
        // Map provider-specific errors to friendly messages
        let errorMessage = "Unable to send verification email. Please try again.";
        
        if (error.message?.includes('rate limit')) {
          errorMessage = "Too many attempts. Please wait a few minutes before trying again.";
        } else if (error.message?.includes('invalid email')) {
          errorMessage = "Please enter a valid email address.";
        } else if (error.message?.includes('already registered')) {
          errorMessage = "This email is already registered. Please use the sign-in form instead.";
        }
        
        throw new Error(errorMessage);
      }

      // Store pending state for resume capability
      localStorage.setItem('safurra_pending_email', email);
      if (data.user?.id) {
        localStorage.setItem('safurra_pending_user_id', data.user.id);
      }
      
      setSignupState(prev => ({
        ...prev,
        email,
        emailSentAt: Date.now(),
        verificationAttempts: 1,
        userId: data.user?.id,
        emailVerified: false,
        hasPassword: false,
        profileComplete: false
      }));

      trackClick('email_confirm_sent', 'authentication');
      setCurrentStep(Step.VerifyEmail);

      toast({
        title: "Confirmation email sent!",
        description: "Please check your inbox and click the confirmation link to continue.",
      });
    } catch (error: any) {
      console.error('Email signup error:', error);
      toast({
        title: "Error",
        description: error.message || "Unable to send verification email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!signupState.email) return;

    const backoffTime = Math.min(30000 * Math.pow(1.5, signupState.verificationAttempts - 1), 300000);
    const timeSinceLastSend = Date.now() - (signupState.emailSentAt || 0);
    
    if (timeSinceLastSend < backoffTime) {
      toast({
        title: "Please wait",
        description: `You can resend in ${Math.ceil((backoffTime - timeSinceLastSend) / 1000)} seconds.`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const redirectTo = getRedirectTo('/onboarding');
      logAuthRedirect('resend-email', redirectTo);

      // For resend, trigger resend confirmation email
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: signupState.email,
        options: {
          emailRedirectTo: redirectTo
        }
      });

      if (error) throw error;

      setSignupState(prev => ({
        ...prev,
        emailSentAt: Date.now(),
        verificationAttempts: prev.verificationAttempts + 1
      }));

      toast({
        title: "Email resent!",
        description: "Check your inbox for the verification link.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to resend email. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = () => {
    // Clear pending state
    localStorage.removeItem('safurra_pending_email');
    localStorage.removeItem('safurra_pending_user_id');
    
    setCurrentStep(Step.EmailOnly);
    setSignupState({ 
      email: '', 
      verificationAttempts: 0,
      emailVerified: false,
      hasPassword: false,
      profileComplete: false
    });
  };

  // Auth state listener to detect verification and handle flow routing
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Clear pending state
          localStorage.removeItem('safurra_pending_email');
          localStorage.removeItem('safurra_pending_user_id');
          
          // Check user's completion status to route correctly
          const { data: profile } = await supabase
            .from('profiles')
            .select('onboarding_completed, first_name, last_name')
            .eq('user_id', session.user.id)
            .maybeSingle();

          const requiresPasswordReset = session.user.user_metadata?.requires_password_reset;
          
          setSignupState(prev => ({
            ...prev,
            userId: session.user.id,
            emailVerified: !!session.user.email_confirmed_at,
            hasPassword: !requiresPasswordReset,
            profileComplete: !!profile?.onboarding_completed
          }));
          
          // Route based on completion status for signup flow
          if (profile?.onboarding_completed) {
            // Fully complete - redirect to dashboard
            onSignupComplete(session.user);
          } else if (!session.user.email_confirmed_at) {
            // Email not verified - keep on verification step
            setCurrentStep(Step.VerifyEmail);
          } else if (requiresPasswordReset) {
            // Email verified but needs password - create password
            setCurrentStep(Step.CreatePassword);
            trackClick('email_confirmed', 'authentication');
          } else if (!profile?.first_name || !profile?.last_name) {
            // Has password but profile incomplete - complete profile
            setCurrentStep(Step.ProfileInfo);
          } else {
            // All complete
            onSignupComplete(session.user);
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [onSignupComplete]);

  const handleProfileComplete = async (profileData: any) => {
    if (!signupState.userId) return;

    setIsLoading(true);
    trackClick('profile_form_submitted', 'onboarding');
    
    try {
      // Save profile data
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          dob: profileData.dob,
          marketing_opt_in: profileData.marketingOptIn,
          // Include initial form data
          name: initialData.name,
          age: parseInt(initialData.age),
          state: initialData.state,
          coverage_amount: initialData.coverageAmount,
          smoking_status: initialData.smokingStatus,
        })
        .eq('user_id', signupState.userId);

      if (error) throw error;

      setSignupState(prev => ({
        ...prev,
        profileComplete: true
      }));

      // After password, complete the flow
      setCurrentStep(Step.Done);
      trackClick('profile_saved', 'onboarding');
      
    } catch (error: any) {
      console.error('Profile save error:', error);
      trackClick('profile_save_error', 'error', { error: error.message });
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordCreated = async () => {
    setSignupState(prev => ({
      ...prev,
      hasPassword: true
    }));

    trackClick('password_created', 'authentication');
    // After password creation, move to profile info
    setCurrentStep(Step.ProfileInfo);
  };

  const handleProfileCompleteAfterPassword = async (profileData: any) => {
    if (!signupState.userId) return;

    setIsLoading(true);
    trackClick('profile_form_submitted', 'onboarding');
    
    try {
      // Save profile data and mark onboarding complete
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          dob: profileData.dob,
          marketing_opt_in: profileData.marketingOptIn,
          onboarding_completed: true,
          // Include initial form data
          name: initialData.name,
          age: parseInt(initialData.age),
          state: initialData.state,
          coverage_amount: initialData.coverageAmount,
          smoking_status: initialData.smokingStatus,
        })
        .eq('user_id', signupState.userId);

      if (error) throw error;

      setSignupState(prev => ({
        ...prev,
        profileComplete: true
      }));

      trackClick('profile_saved', 'onboarding');
      trackClick('onboarding_completed', 'onboarding');
      
      const { data: { user } } = await supabase.auth.getUser();
      onSignupComplete(user);
      
    } catch (error: any) {
      console.error('Profile save error:', error);
      trackClick('profile_save_error', 'error', { error: error.message });
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case Step.EmailOnly:
        return (
          <EmailOnlyStep
            onEmailSubmit={handleEmailSubmit}
            onGoogleAuth={handleGoogleAuth}
            onPhoneAuth={handlePhoneAuth}
            isLoading={isLoading}
          />
        );
      case Step.VerifyEmail:
        return (
          <EmailVerificationStep
            email={signupState.email}
            onResend={handleResendEmail}
            onChangeEmail={handleChangeEmail}
            onVerified={() => {}} // Handled by auth state listener
            emailSentAt={signupState.emailSentAt}
            isLoading={isLoading}
          />
        );
      case Step.CreatePassword:
        return (
          <CreatePasswordStep
            onPasswordCreated={handlePasswordCreated}
            isLoading={isLoading}
          />
        );
      case Step.ProfileInfo:
        return (
          <ProfileInfoStep
            onProfileComplete={handleProfileCompleteAfterPassword}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
      <CardHeader className="text-center pb-6">
        <div className="mx-auto mb-4 bg-primary/10 p-3 rounded-full w-fit">
          <Shield className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold text-foreground">
          {currentStep === Step.EmailOnly && "Create Your Account"}
          {currentStep === Step.VerifyEmail && "Verify your email"}
          {currentStep === Step.CreatePassword && "Create a password"}
          {currentStep === Step.ProfileInfo && "About you"}
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Join thousands saving on life insurance
        </CardDescription>
      </CardHeader>
      <CardContent>
        {renderStep()}
      </CardContent>
    </Card>
  );
};